import React, { useContext, useEffect, useRef } from 'react';
import lottie from 'lottie-web';
import useStyles from 'isomorphic-style-loader/useStyles';

import { DotIndicator, Button } from '@sololearnorg/sol';

import { Container, i18n } from '../../../../symphony';
import { useContainerData } from '../../../../shared/public/SlHooks/useContainerData';
import { SlAISolutionsContext } from '../../global-constants';
import { SlActions } from '../../services/ActionsService/sl-actions';
import { ActionType, RequestType } from '../../global-interface';
import { SlDataService } from '../../services/sl-data.service';
import { KodieContext } from '../../services/context';
import KodieHint from '../../../../shared/public/assets/lottieAnimations/KodieHint.json';
import { RectangleIcon } from '../../../../shared/public/assets/icons/RectangleIcon';
import { MaxIcon } from '../../../../_paywalls/private/components/ui/icons';

import s from './AIAnswerExplanation.scss';

export function AIAnswerExplanation() {
  useStyles(s);
  const { context } = useContext(KodieContext);
  const { actions$ } = Container.take(SlAISolutionsContext, SlActions);
  const animationRef = useRef<HTMLDivElement>();
  const {
    answerExplanationRes$: answerExplanationRes,
    isAnswerExplanationResWithError$: isAnswerExplanationResWithError,
    isAnswerExplanationDataLoading$: isAnswerExplanationDataLoading,
    remainingAttemptsCountData$: remainingAttemptsCountData,
  } = useContainerData(SlAISolutionsContext, SlDataService, [
    'answerExplanationRes$',
    'isAnswerExplanationDataLoading$',
    'isAnswerExplanationResWithError$',
    'remainingAttemptsCountData$',
  ]);
  const getAIResponseClickHandler = () => {
    actions$.next({
      type: ActionType.OnGetAIResponseInBottomSheetClick,
      requestType: RequestType.Parallel,
      data: {
        solutionContext: context,
      },
    });
  };

  useEffect(() => {
    if (animationRef.current) {
      lottie.loadAnimation({
        container: animationRef.current,
        loop: true,
        autoplay: true,
        animationData: KodieHint,
      });
    }
  }, [animationRef]);

  const getDescContent = () => {
    if (isAnswerExplanationDataLoading) {
      return <DotIndicator className="loading-fullscreen" />;
    }

    if (isAnswerExplanationResWithError) {
      return (
        <div className="ai-answer-explanation__desc__error">
          <div>
            <p>{i18n.t('web-kodie.something-went-wrong-error-message')}</p>
          </div>
          <Button
            size="s"
            onClick={getAIResponseClickHandler}
            disabled={!remainingAttemptsCountData?.length}
          >
            {i18n.t('web-kodie.ask-again-button')}
          </Button>
        </div>
      );
    }

    if (answerExplanationRes) {
      return (
        <div className="ai-answer-explanation__desc__response">
          {answerExplanationRes}
        </div>
      );
    }

    return (
      <>
        {i18n.t('web-kodie.bottom-sheet-title-for-not-pro',)}
        {' '}
        <button
          type="button"
          className="ai-answer-explanation__desc__button"
          onClick={getAIResponseClickHandler}
          disabled={!remainingAttemptsCountData?.length}
        >
          {i18n.t('web-kodie.bottom-sheet-cta')}
        </button>
      </>
    );
  };

  return (
    <div className="ai-answer-explanation">
      <div
        ref={animationRef}
        className="ai-answer-explanation__animation"
      >
        <div><MaxIcon /></div>
      </div>
      <div className="ai-answer-explanation__desc__wrapper">
        <RectangleIcon className="ai-answer-explanation__desc__rect-icon" />
        <div
          className={`ai-answer-explanation__desc${isAnswerExplanationDataLoading ? ' loading' : ''
            }`}
        >
          {getDescContent()}
        </div>
      </div>
    </div>
  );
}
